import constants from '@/constants';

import DataSet from './DataSet';
import OrderedPair from './OrderedPair';
import TimeSeries from './TimeSeries';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Handling invalid data
 *
 * types
 * - missing datapoints
 * - -999999 values
 * - ice?
 * - possibly other stuff?
 *
 * Background
 *
 * - turns out this is pretty easy to solve... using ECharts markArea
 *
 * https://echarts.apache.org/examples/en/editor.html?c=line-sections
 *
 */


/**
 * Take two on a .ts Site abstraction.
 *
 * Needs to be built progressively b/c there's four APIs involved...
 */
export default class SiteData {
  discharge: DataSet
  gaugeHeight: DataSet
  waterTemp: DataSet

  constructor() {
    this.discharge = { parameterCd: constants.FlowVariables.Discharge.parameterCd };
    this.gaugeHeight = { parameterCd: constants.FlowVariables.GaugeHeight.parameterCd };
    this.waterTemp = { parameterCd: constants.SecondaryVariables.WaterTemp.parameterCd };
  }

  setIvDataSet(ivResponse: any) {
    if (ivResponse.flowVariables.Discharge) {
      this.discharge.rawInstantTimeSeries = { data: ivResponse.flowVariables.Discharge };
      const rawTimeSeries = this.discharge.rawInstantTimeSeries;
      if (rawTimeSeries.data && rawTimeSeries.data.length > 0) {
        this.discharge = {
          ...this.discharge,
          ...this._buildIvTimeSeries(rawTimeSeries)
        }
      }
    }

    if (ivResponse.flowVariables.GaugeHeight) {
      this.gaugeHeight.rawInstantTimeSeries = { data: ivResponse.flowVariables.GaugeHeight };
      const rawTimeSeries = this.gaugeHeight.rawInstantTimeSeries;
      if (rawTimeSeries.data && rawTimeSeries.data.length > 0) {
        this.gaugeHeight = {
          ...this.gaugeHeight,
          ...this._buildIvTimeSeries(rawTimeSeries)
        }
      }
    }

    if (ivResponse.secondaryVariables.WaterTemp) {
      this.waterTemp.rawInstantTimeSeries = { data: ivResponse.secondaryVariables.WaterTemp };
      const rawTimeSeriesWater = this.waterTemp.rawInstantTimeSeries;
      if (rawTimeSeriesWater.data && rawTimeSeriesWater.data.length > 0) {
        this.waterTemp = {
          ...this.waterTemp,
          ...this._buildIvTimeSeries(rawTimeSeriesWater)
        }
      }
    }
  }

  _buildIvTimeSeries(rawTimeSeries: TimeSeries) {
    return {
      lastDayTimeSeries: { data: this._getLastNDaysTimeSeries(rawTimeSeries.data, 1) },
      lastWeekTimeSeries: { data: this._getLastNDaysTimeSeries(rawTimeSeries.data, 7) },
      lastMonthTimeSeries: { data: this._getLastNDaysTimeSeries(rawTimeSeries.data, 30) },
    }
  }

  _getLastNDaysTimeSeries(timeSeries: Array<OrderedPair>, numDays: number) {
    const timeSeriesLength = timeSeries.length;


    // TODO: this is the problem!
    const now = new Date(timeSeries[timeSeriesLength - 1].x);

    // Get the lower bound time stamp for the matching set
    const since = new Date(now.getTime() - ONE_DAY_MS * numDays);

    return timeSeries.filter((pair: OrderedPair) => {
      return new Date(pair.x) >= since;
    })
  }

  setDvDataSet(dvResponse: any) {
    if (dvResponse.flowVariables.Discharge) {
      this.discharge.rawDailyTimeSeries = { data: dvResponse.flowVariables.Discharge };

      // for a one day data set,
      //
      // new Date(this.discharge.rawInstantTimeSeries.data[len -1].x) - new Date(this.discharge.rawInstantTimeSeries.data[0].x )

      // --> 8640000 miliseconds
      const rawTimeSeries = this.discharge.rawDailyTimeSeries;

      if (rawTimeSeries.data && rawTimeSeries.data.length > 0) {
        this.discharge = {
          ...this.discharge,
          ...this._buildDvTimeSeries(rawTimeSeries)
        }
      }
    }

    if (dvResponse.flowVariables.GaugeHeight) {
      this.gaugeHeight.rawDailyTimeSeries = { data: dvResponse.flowVariables.GaugeHeight };
      const rawTimeSeries = this.gaugeHeight.rawDailyTimeSeries;

      if (rawTimeSeries.data && rawTimeSeries.data.length > 0) {
        this.gaugeHeight = {
          ...this.gaugeHeight,
          ...this._buildDvTimeSeries(rawTimeSeries)
        }
      }
    }

    if (dvResponse.secondaryVariables.WaterTemp) {
      this.waterTemp.rawDailyTimeSeries = { data: dvResponse.secondaryVariables.WaterTemp };
      const rawTimeSeries = this.waterTemp.rawDailyTimeSeries;

      if (rawTimeSeries.data && rawTimeSeries.data.length > 0) {
        this.waterTemp = {
          ...this.waterTemp,
          ...this._buildDvTimeSeries(rawTimeSeries)
        }
      }
    }
  }

  _buildDvTimeSeries(rawTimeSeries: TimeSeries) {
    return {
      last3MonthsTimeSeries: { data: this._getLastNDaysTimeSeries(rawTimeSeries.data, 90) },
      last6MonthsTimeSeries: { data: this._getLastNDaysTimeSeries(rawTimeSeries.data, 180) },
      lastYearTimeSeries: { data: this._getLastNDaysTimeSeries(rawTimeSeries.data, 365) },
    }
  }

  setHistoricalDataSet(historicalResponse: any) {
    if (historicalResponse.flowVariables.Discharge) {
      this.discharge.historicalDataMap = historicalResponse.flowVariables.Discharge;
    }

    if (historicalResponse.flowVariables.GaugeHeight) {
      this.gaugeHeight.historicalDataMap = historicalResponse.flowVariables.GaugeHeight;
    }
  }

}
